/**
 * ExtendedParagraphNode adds a block-level inline `style` (used for paragraph
 * spacing) on top of the stock Lexical ParagraphNode. It is registered via the
 * official node-replacement API (`replace` + `withKlass`) so that every
 * paragraph created in the editor becomes an ExtendedParagraphNode, and old
 * documents (type "paragraph") transparently upgrade on load.
 *
 * Because the replacement uses a distinct node type ("extended-paragraph"),
 * the few places that compare `getType() === 'paragraph'` are updated to also
 * accept this type (or use the instanceof-based `$isParagraphNode`).
 */

import { ParagraphNode, $applyNodeReplacement } from 'lexical';
import { applyBlockStyle, getBlockStyleObject } from './blockStyle';

export class ExtendedParagraphNode extends ParagraphNode {
    constructor(style = '', key) {
        super(key);
        this.__style = style;
    }

    static getType() {
        return 'extended-paragraph';
    }

    static clone(node) {
        return new ExtendedParagraphNode(node.__style, node.__key);
    }

    getStyle() {
        return this.getLatest().__style;
    }

    setStyle(style) {
        const self = this.getWritable();
        self.__style = style || '';
        return self;
    }

    createDOM(config) {
        const dom = super.createDOM(config);
        if (this.__style) {
            applyBlockStyle(dom, '', this.__style);
        }
        return dom;
    }

    updateDOM(prevNode, dom, config) {
        const needsReplace = super.updateDOM(prevNode, dom, config);
        if (prevNode.__style !== this.__style) {
            applyBlockStyle(dom, prevNode.__style || '', this.__style || '');
        }
        return needsReplace;
    }

    static importJSON(serializedNode) {
        const node = $createExtendedParagraphNode(serializedNode.style || '');
        node.setFormat(serializedNode.format);
        node.setIndent(serializedNode.indent);
        node.setDirection(serializedNode.direction);
        if (typeof serializedNode.textFormat === 'number' && node.setTextFormat) {
            node.setTextFormat(serializedNode.textFormat);
        }
        return node;
    }

    exportJSON() {
        return {
            ...super.exportJSON(),
            type: 'extended-paragraph',
            version: 1,
            style: this.__style || '',
        };
    }

    exportDOM(editor) {
        const out = super.exportDOM(editor);
        const el = out && out.element;
        if (el && el.style && this.__style) {
            const obj = getBlockStyleObject(this.__style);
            for (const [k, v] of Object.entries(obj)) {
                el.style.setProperty(k, v);
            }
        }
        return out;
    }
}

export function $createExtendedParagraphNode(style = '') {
    return $applyNodeReplacement(new ExtendedParagraphNode(style));
}

export function $isExtendedParagraphNode(node) {
    return node instanceof ExtendedParagraphNode;
}
